import { Button } from "@/components/ui/button";
import { Upload, FileText, X } from "lucide-react";
import { useRef, useState } from "react";
import { useToast } from "@/hooks/use-toast";

interface DocumentSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDocumentsAdd: (files: File[]) => void;
  singleFileMode?: boolean;
}

const DocumentSelectionModal = ({ isOpen, onClose, onDocumentsAdd, singleFileMode = false }: DocumentSelectionModalProps) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const handleFileSelect = (files: FileList | null) => {
    if (!files) return;

    const pdfFiles = Array.from(files).filter(file => file.type === 'application/pdf');
    
    if (pdfFiles.length === 0) {
      toast({
        title: "Invalid file type",
        description: "Please select PDF files only",
        variant: "destructive",
      });
      return;
    }

    if (singleFileMode && pdfFiles.length > 1) {
      toast({
        title: "Single file only",
        description: "Please select only one PDF file",
        variant: "destructive",
      });
      return;
    }

    if (!singleFileMode && pdfFiles.length > 50) {
      toast({
        title: "Too many files",
        description: "Please select up to 50 PDF files at a time",
        variant: "destructive",
      });
      return;
    }

    setSelectedFiles(singleFileMode ? [pdfFiles[0]] : pdfFiles);
  };

  const handleSubmit = () => {
    if (selectedFiles.length === 0) return;
    onDocumentsAdd(selectedFiles);
    handleClose();
  };

  const handleClose = () => {
    setSelectedFiles([]);
    onClose();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    handleFileSelect(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-slate-900">
              {singleFileMode ? 'Select PDF Document' : 'Select PDF Documents'}
            </h3>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="space-y-4">
          {/* File Input Area */}
          <div 
            className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center cursor-pointer hover:border-slate-400 transition-colors"
            onClick={() => fileInputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
          >
            <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
            <p className="text-sm text-slate-600 mb-1">
              Click to select or drag and drop {singleFileMode ? 'a PDF file' : 'PDF files'}
            </p>
            {!singleFileMode && (
              <p className="text-xs text-slate-500">
                You can select multiple files (up to 50)
              </p>
            )}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf"
            multiple={!singleFileMode}
            onChange={(e) => handleFileSelect(e.target.files)}
            className="hidden"
          />

          {/* Selected Files Preview */}
          {selectedFiles.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-slate-700">
                {selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''} selected
              </p>
              <div className="max-h-32 overflow-y-auto space-y-1">
                {selectedFiles.map((file, index) => (
                  <div key={index} className="text-xs text-slate-600 truncate">
                    {file.name}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={handleClose} className="flex-1">
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit}
              disabled={selectedFiles.length === 0}
              className="flex-1"
            >
              {singleFileMode 
                ? 'Create Topic & Upload' 
                : 'Add PDFs'
              }
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentSelectionModal;
